import { cn } from "@/shared/utils/classname-merger";

export type TabType = 'documents' | 'questions';

interface ThirdPartyHeaderProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  progressPercentage?: number;
  isSaving?: boolean;
}

const ThirdPartyHeader: React.FC<ThirdPartyHeaderProps> = ({
  activeTab,
  onTabChange,
  progressPercentage = 15,
  isSaving = false
}) => {
  return (
    <div 
      className="flex flex-col items-start p-0 w-full h-[52px] bg-white rounded-t-lg"
      style={{ 
        boxSizing: 'border-box',
        flex: 'none',
        order: 0,
        alignSelf: 'stretch',
        flexGrow: 0,
        zIndex: 3
      }}
    >
      {/* Main Frame 6326 */}
      <div 
        className="flex flex-row items-center p-0 w-full h-[52px] bg-white rounded-t-lg"
        style={{
          boxSizing: 'border-box',
          flex: 'none',
          order: 0,
          alignSelf: 'stretch',
          flexGrow: 0
        }}
      >
        {/* Left Section - Frame 6324 */}
        <div 
          className="flex flex-row items-center px-4 py-0 gap-3 h-[52px] flex-1"
          style={{
            width: '440.33px',
            flex: 'none',
            order: 0,
            flexGrow: 1
          }}
        >
          {/* Frame 6016 - Title and Status */}
          <div 
            className="flex flex-row items-center p-0 gap-4 w-[285px] h-5"
            style={{
              flex: 'none',
              order: 0,
              flexGrow: 0
            }}
          >
            {/* Complete Your Assessment Text */}
            <h2 
              className="w-[185px] h-5 text-[#171717] font-medium text-sm leading-5"
              style={{
                fontFamily: 'Inter',
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                flex: 'none',
                order: 0,
                flexGrow: 0
              }}
            >
              Complete Your Submission
            </h2>
            
            {/* Edit Status - Frame with spinner and auto-saving text - Only show when saving */}
            {isSaving && (
              <div 
                className="flex flex-row items-center p-0 gap-0.5 w-[84px] h-5"
                style={{
                  flex: 'none',
                  order: 1,
                  flexGrow: 0
                }}
              >
                {/* Spinner Icon */}
                <div 
                  className="w-4 h-4 relative"
                  style={{
                    flex: 'none',
                    order: 1,
                    flexGrow: 0
                  }}
                >
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 16 16" 
                    fill="none" 
                    className="animate-spin"
                  >
                    <path d="M8 1.5V4.5" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" opacity="0.93"/>
                    <path d="M8 11.5V14.5" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" opacity="0.3"/>
                    <path d="M11.5 8H14.5" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" opacity="0.3"/>
                    <path d="M1.5 8H4.5" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" opacity="0.65"/>
                    <path d="M11.1213 4.87868L13.2426 2.75736" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" opacity="0.3"/>
                    <path d="M2.75736 13.2426L4.87868 11.1213" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" opacity="0.3"/>
                    <path d="M11.1213 11.1213L13.2426 13.2426" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" opacity="0.3"/>
                    <path d="M2.75736 2.75736L4.87868 4.87868" stroke="#71717A" strokeWidth="1.5" strokeLinecap="round" opacity="0.86"/>
                  </svg>
                </div>
                
                {/* Auto-saving Text */}
                <span 
                  className="w-8 h-5 text-[#71717A] text-[10px] leading-5 opacity-50"
                  style={{
                    fontFamily: 'Inter',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '10px',
                    lineHeight: '20px',
                    flex: 'none',
                    order: 2,
                    flexGrow: 0
                  }}
                >
                  Saving
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Center Section - Frame 6310 Tab Switcher */}
        <div 
          className="flex flex-row justify-center items-center p-0 h-8 flex-1"
          style={{
            width: '440.33px',
            flex: 'none',
            order: 1,
            flexGrow: 1
          }}
        >
          {/* Frame 6316 - Tab Container */}
          <div 
            className="flex flex-row items-center p-0.5 gap-0.5 w-[194px] h-8 bg-[#F4F4F5] rounded-lg"
            style={{
              flex: 'none',
              order: 0,
              flexGrow: 0
            }}
          >
            {/* Documents Tab - Frame 6315 */}
            <button
              type="button"
              onClick={() => onTabChange('documents')}
              className={cn(
                "flex flex-col justify-center items-center px-4 py-1 gap-1 w-[98px] h-7 rounded-md relative transition-all duration-200",
                activeTab === 'documents'
                  ? "bg-white shadow-sm"
                  : "bg-transparent"
              )}
              style={{
                isolation: 'isolate',
                flex: 'none',
                order: 0,
                flexGrow: 0,
                boxShadow: activeTab === 'documents' ? '0px 2px 4px rgba(0, 0, 0, 0.1)' : 'none'
              }}
            >
              <span 
                className={cn(
                  "w-[66px] h-5 text-xs leading-5 font-medium",
                  activeTab === 'documents' ? "text-[#18181B]" : "text-[#3F3F46]"
                )}
                style={{
                  fontFamily: 'Inter',
                  fontStyle: 'normal',
                  fontWeight: activeTab === 'documents' ? 500 : 400,
                  fontSize: '12px',
                  lineHeight: '20px',
                  flex: 'none',
                  order: 0,
                  flexGrow: 0,
                  zIndex: 0
                }}
              >
                Documents
              </span>
            </button>
            
            {/* Questions Tab - Frame 6314 */}
            <button
              type="button"
              onClick={() => onTabChange('questions')}
              className={cn(
                "flex flex-col justify-center items-center px-4 py-1 gap-1 w-[90px] h-7 rounded-md transition-all duration-200",
                activeTab === 'questions'
                  ? "bg-white shadow-sm"
                  : "bg-transparent"
              )}
              style={{
                flex: 'none',
                order: 1,
                flexGrow: 0
              }}
            >
              <span 
                className={cn(
                  "w-[58px] h-5 text-xs leading-5",
                  activeTab === 'questions' ? "text-[#18181B] font-medium" : "text-[#3F3F46] font-normal"
                )}
                style={{
                  fontFamily: 'Inter',
                  fontStyle: 'normal',
                  fontWeight: activeTab === 'questions' ? 500 : 400,
                  fontSize: '12px',
                  lineHeight: '20px',
                  flex: 'none',
                  order: 0,
                  flexGrow: 0
                }}
              >
                Questions
              </span>
            </button>
          </div>
        </div>

        {/* Right Section - Frame 6326 Progress */}
        <div 
          className="flex flex-row justify-end items-center px-4 py-3 gap-4 h-11 flex-1"
          style={{
            width: '440.33px',
            flex: 'none',
            order: 2,
            flexGrow: 1
          }}
        >
          {/* Progress Percentage */}
          <span 
            className="w-[110px] h-5 text-[#3F3F46] text-xs leading-5 text-right"
            style={{
              fontFamily: 'Inter',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '20px',
              flex: 'none',
              order: 1,
              flexGrow: 0
            }}
          >
            {progressPercentage}%
          </span>
          
          {/* Progress Bar - Frame 6301 */}
          <div 
            className="flex flex-row items-center p-0 w-[240px] h-3 border border-white rounded-lg"
            style={{
              isolation: 'isolate',
              flex: 'none',
              order: 2,
              flexGrow: 0
            }}
          >
            {/* Progress Fill - Rectangle 1286 */}
            <div 
              className="h-3 bg-[#006FEE] border border-white rounded-l-lg"
              style={{
                width: `${(progressPercentage / 100) * 240}px`,
                maxWidth: '240px',
                flex: 'none',
                order: 0,
                flexGrow: 0,
                zIndex: 1,
                borderRadius: progressPercentage === 100 ? '8px' : '8px 0px 0px 8px'
              }}
            />
            {/* Progress Background - Rectangle 1287 */}
            <div 
              className="h-3 bg-[#F4F4F5] rounded-r-lg flex-1"
              style={{
                width: `${240 - (progressPercentage / 100) * 240}px`,
                flex: 'none',
                order: 1,
                flexGrow: 1,
                zIndex: 0,
                borderRadius: progressPercentage === 0 ? '8px' : '0px 8px 8px 0px'
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThirdPartyHeader;